import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Stack,
  alpha,
  styled,
} from '@mui/material';
import {
  Search as SearchIcon,
  Science as TestIcon,
  CheckCircle as SelectedIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { Test } from '../../types/test.types';

const colors = {
  gold: '#C5A572',
  goldLight: '#E5D3B5',
  goldDark: '#8B7355',
  burgundy: '#800020',
  navy: '#1A1F2E',
  cream: '#FDFAF3',
  emerald: '#046307',
};

// Fixed styled component - removed unused theme parameter
const LuxuryCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ selected }) => ({
  background: selected
    ? `linear-gradient(135deg, ${alpha(colors.gold, 0.1)} 0%, ${alpha(colors.burgundy, 0.1)} 100%)`
    : colors.cream,
  border: selected ? `2px solid ${colors.gold}` : `1px solid ${alpha(colors.gold, 0.3)}`,
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 10px 30px -10px ${alpha(colors.gold, 0.3)}`,
    borderColor: colors.gold,
  },
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: 'rgba(255,255,255,0.9)',
    '&:hover': {
      backgroundColor: '#FFFFFF',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.gold,
      borderWidth: '2px',
    },
  },
});

const ITEMS_PER_PAGE = 20;

interface TestCatalogProps {
  tests: Test[];
  selectedTests: Test[];
  onTestSelect: (test: Test) => void;
  loading?: boolean;
}

export const TestCatalog: React.FC<TestCatalogProps> = ({
  tests,
  selectedTests,
  onTestSelect,
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Get unique categories
  const categories = ['all', ...new Set(tests.map(t => t.categories).filter(Boolean))];

  // Filter tests
  const filteredTests = tests.filter(test => {
    const matchesSearch = test.TestName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         test.TestCode?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || test.categories === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Paginate
  const totalPages = Math.ceil(filteredTests.length / ITEMS_PER_PAGE);
  const paginatedTests = filteredTests.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const isSelected = (testId: number) => selectedTests.some(t => t.TestId === testId);

  return (
    <Box>
      {/* Search and Filter Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StyledTextField
          placeholder="Search tests..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.gold }} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 2, minWidth: '200px' }}
        />
        <FormControl sx={{ flex: 1, minWidth: '150px' }}>
          <InputLabel sx={{ color: colors.navy }}>Category</InputLabel>
          <Select
            value={categoryFilter}
            label="Category"
            onChange={(e) => setCategoryFilter(e.target.value)}
            sx={{
              borderRadius: '8px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(colors.gold, 0.3),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.gold,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.gold,
                borderWidth: '2px',
              },
            }}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {categories.slice(1).map(cat => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Test Grid */}
      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Grid container spacing={2}>
            <AnimatePresence>
              {paginatedTests.map((test) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={test.TestId}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <LuxuryCard selected={isSelected(test.TestId)} onClick={() => onTestSelect(test)}>
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <TestIcon sx={{ color: colors.gold }} />
                          {isSelected(test.TestId) && (
                            <SelectedIcon sx={{ color: colors.emerald }} />
                          )}
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.navy }}>
                          {test.TestName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Code: {test.TestCode}
                        </Typography>
                        <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                          <Chip
                            label={`₹${test.Amount}`}
                            size="small"
                            sx={{
                              backgroundColor: alpha(colors.gold, 0.2),
                              color: colors.navy,
                              fontWeight: 600,
                            }}
                          />
                          {test.categories && (
                            <Chip
                              label={test.categories}
                              size="small"
                              variant="outlined"
                              sx={{ borderColor: colors.goldLight }}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </LuxuryCard>
                  </motion.div>
                </Grid>
              ))}
            </AnimatePresence>
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Stack spacing={2} sx={{ mt: 4, alignItems: 'center' }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: colors.navy,
                    '&.Mui-selected': {
                      backgroundColor: colors.gold,
                      color: colors.cream,
                    },
                  },
                }}
              />
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

// import React, { useState, useMemo } from 'react';
// import {
//   Box,
//   TextField,
//   Grid,
//   Paper,
//   Typography,
//   Chip,
//   InputAdornment,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemSecondaryAction,
//   IconButton,
//   Divider,
//   Badge,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   SelectChangeEvent,
// } from '@mui/material';
// import {
//   Search as SearchIcon,
//   Add as AddIcon,
//   Remove as RemoveIcon,
//   Science as ScienceIcon,
// } from '@mui/icons-material';
// import { Test } from '../../types/registration.types';

// interface TestCatalogProps {
//   tests: Test[];
//   selectedTests: Test[];
//   onTestSelect: (test: Test) => void;
//   loading?: boolean;
// }

// // Define category colors for visual distinction
// const categoryColors: Record<string, string> = {
//   'HAEMATOLOGY': '#1976d2',
//   'BIO_CHEMISTRY': '#2e7d32',
//   'RADIOLOGY': '#ed6c02',
//   'CARDIAC': '#9c27b0',
//   'THYROID': '#d32f2f',
//   'OTHER': '#757575',
// };

// export const TestCatalog: React.FC<TestCatalogProps> = ({
//   tests,
//   selectedTests,
//   onTestSelect,
//   loading = false,
// }) => {
//   const [searchTerm, setSearchTerm] = useState('');
//   const [selectedCategory, setSelectedCategory] = useState('all');

//   // Generate unique categories from test data
//   const categories = useMemo(() => {
//     const uniqueCategories = new Set<string>();
    
//     // Add 'all' option
//     const categoryOptions = [{ id: 'all', label: 'All Tests' }];
    
//     // Extract unique categories from tests
//     tests.forEach(test => {
//       if (test.categories) {
//         uniqueCategories.add(test.categories);
//       }
//     });
    
//     // Convert to array and sort
//     const sortedCategories = Array.from(uniqueCategories).sort();
    
//     // Add to options
//     sortedCategories.forEach(cat => {
//       categoryOptions.push({ 
//         id: cat, 
//         label: cat.replace(/_/g, ' ') // Replace underscores with spaces for display
//       });
//     });
    
//     return categoryOptions;
//   }, [tests]);

//   // Filter tests based on search and category
//   const filteredTests = tests.filter(test => {
//     const matchesSearch = 
//       (test.TestName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
//       (test.TestCode?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
//     const matchesCategory = selectedCategory === 'all' || test.categories === selectedCategory;
    
//     return matchesSearch && matchesCategory;
//   });

//   // Calculate total price of selected tests
//   const getTotalSelectedPrice = () => {
//     return selectedTests.reduce((sum, test) => sum + (test.Amount || 0), 0);
//   };

//   // Get color for category chip
//   const getCategoryColor = (category: string | undefined): string => {
//     if (!category) return '#757575';
//     return categoryColors[category] || '#757575';
//   };

//   return (
//     <Grid container spacing={2}>
//       {/* Left Column - Test Catalog */}
//       <Grid size={{ xs: 12, md: 8 }}>
//         <Paper variant="outlined" sx={{ p: 2 }}>
//           <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
//             <TextField
//               sx={{ flex: 2, minWidth: '200px' }}
//               placeholder="Search tests by name or code..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               InputProps={{
//                 startAdornment: (
//                   <InputAdornment position="start">
//                     <SearchIcon />
//                   </InputAdornment>
//                 ),
//               }}
//               size="small"
//             />
            
//             <FormControl sx={{ flex: 1, minWidth: '150px' }} size="small">
//               <InputLabel>Category</InputLabel>
//               <Select
//                 value={selectedCategory}
//                 label="Category"
//                 onChange={(e: SelectChangeEvent) => setSelectedCategory(e.target.value)}
//               >
//                 {categories.map((cat) => (
//                   <MenuItem key={cat.id} value={cat.id}>
//                     {cat.label}
//                   </MenuItem>
//                 ))}
//               </Select>
//             </FormControl>
//           </Box>

//           {loading ? (
//             <Box sx={{ p: 3, textAlign: 'center' }}>
//               <Typography color="textSecondary">Loading tests...</Typography>
//             </Box>
//           ) : (
//             <List sx={{ maxHeight: 400, overflow: 'auto' }}>
//               {filteredTests.length === 0 ? (
//                 <ListItem>
//                   <ListItemText 
//                     primary="No tests found" 
//                     secondary="Try adjusting your search or category filter"
//                   />
//                 </ListItem>
//               ) : (
//                 filteredTests.map((test) => {
//                   const isSelected = selectedTests.some(t => t.TestId === test.TestId);
                  
//                   return (
//                     <React.Fragment key={test.TestId}>
//                       <ListItem
//                         onClick={() => onTestSelect(test)}
//                         sx={{
//                           cursor: 'pointer',
//                           bgcolor: isSelected ? 'action.selected' : 'inherit',
//                           '&:hover': {
//                             bgcolor: isSelected ? 'action.selected' : 'action.hover',
//                           }
//                         }}
//                       >
//                         <ListItemText
//                           primary={
//                             <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
//                               <ScienceIcon fontSize="small" color="primary" />
//                               <Typography variant="body1" sx={{ fontWeight: 500 }}>
//                                 {test.TestName}
//                               </Typography>
//                               {test.categories && (
//                                 <Chip 
//                                   label={test.categories.replace(/_/g, ' ')} 
//                                   size="small" 
//                                   sx={{
//                                     backgroundColor: getCategoryColor(test.categories),
//                                     color: 'white',
//                                     fontSize: '0.7rem',
//                                     height: 20
//                                   }}
//                                 />
//                               )}
//                             </Box>
//                           }
//                           secondary={
//                             <Box>
//                               <Typography variant="caption" color="textSecondary">
//                                 Code: {test.TestCode}
//                               </Typography>
//                               {test.Amount && (
//                                 <Typography variant="body2" color="primary" sx={{ mt: 0.5, fontWeight: 600 }}>
//                                   ₹{test.Amount}
//                                 </Typography>
//                               )}
//                             </Box>
//                           }
//                         />
//                         <ListItemSecondaryAction>
//                           <IconButton
//                             edge="end"
//                             color={isSelected ? 'error' : 'primary'}
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               onTestSelect(test);
//                             }}
//                             size="small"
//                           >
//                             {isSelected ? <RemoveIcon /> : <AddIcon />}
//                           </IconButton>
//                         </ListItemSecondaryAction>
//                       </ListItem>
//                       <Divider />
//                     </React.Fragment>
//                   );
//                 })
//               )}
//             </List>
//           )}
//         </Paper>
//       </Grid>

//       {/* Right Column - Selected Tests */}
//       <Grid size={{ xs: 12, md: 4 }}>
//         <Paper variant="outlined" sx={{ p: 2, position: 'sticky', top: 20 }}>
//           <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
//             Selected Tests
//             <Badge 
//               badgeContent={selectedTests.length} 
//               color="primary"
//               sx={{ ml: 2 }}
//             />
//           </Typography>

//           <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
//             {selectedTests.map((test) => (
//               <ListItem 
//                 key={test.TestId} 
//                 secondaryAction={
//                   <IconButton 
//                     edge="end" 
//                     size="small" 
//                     onClick={() => onTestSelect(test)} 
//                     color="error"
//                   >
//                     <RemoveIcon />
//                   </IconButton>
//                 }
//                 sx={{ pr: 6 }}
//               >
//                 <ListItemText
//                   primary={
//                     <Box display="flex" alignItems="center" gap={1}>
//                       <Typography variant="body2" sx={{ fontWeight: 500 }}>
//                         {test.TestName}
//                       </Typography>
//                       {test.categories && (
//                         <Chip 
//                           label={test.categories} 
//                           size="small" 
//                           sx={{
//                             backgroundColor: getCategoryColor(test.categories),
//                             color: 'white',
//                             fontSize: '0.6rem',
//                             height: 18
//                           }}
//                         />
//                       )}
//                     </Box>
//                   }
//                   secondary={test.TestCode}
//                 />
//                 <Typography variant="body2" sx={{ mr: 4, fontWeight: 600, color: 'primary.main' }}>
//                   ₹{test.Amount || 0}
//                 </Typography>
//               </ListItem>
//             ))}

//             {selectedTests.length === 0 && (
//               <ListItem>
//                 <ListItemText
//                   primary="No tests selected"
//                   secondary="Select tests from the list on the left"
//                   sx={{ textAlign: 'center', py: 3 }}
//                 />
//               </ListItem>
//             )}
//           </List>

//           <Divider sx={{ my: 2 }} />

//           <Box display="flex" justifyContent="space-between" alignItems="center">
//             <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Total:</Typography>
//             <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
//               ₹{getTotalSelectedPrice()}
//             </Typography>
//           </Box>

//           {selectedTests.length > 0 && (
//             <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
//               {selectedTests.length} test{selectedTests.length !== 1 ? 's' : ''} selected
//             </Typography>
//           )}
//         </Paper>
//       </Grid>
//     </Grid>
//   );
// };

// import React, { useState } from 'react';
// import {
//   Paper,
//   Typography,
//   List,
//   ListItem,
//   ListItemText,
//   ListItemSecondaryAction,
//   Checkbox,
//   Divider,
//   Box,
//   TextField,
//   InputAdornment,
//   Chip,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Grid,
//   SelectChangeEvent,
// } from '@mui/material';
// import { Search as SearchIcon, Science as ScienceIcon } from '@mui/icons-material';

// interface Test {
//   id: number;
//   testCode: string;
//   testName: string;
//   price: number;
//   category?: string;
// }

// interface TestCatalogProps {
//   tests: Test[];
//   selectedTests: Test[];
//   onTestSelect: (test: Test) => void;
//   loading?: boolean;
// }

// export const TestCatalog: React.FC<TestCatalogProps> = ({ 
//   tests, 
//   selectedTests, 
//   onTestSelect,
//   loading = false 
// }) => {
//   const [searchTerm, setSearchTerm] = useState('');
//   const [categoryFilter, setCategoryFilter] = useState('all');

//   // Get unique categories
//   const categories = ['all', ...new Set(tests.map(t => t.category || 'Other').filter(Boolean))];

//   const filteredTests = tests.filter(test => {
//     const matchesSearch = test.testName.toLowerCase().includes(searchTerm.toLowerCase()) ||
//                          test.testCode.toLowerCase().includes(searchTerm.toLowerCase());
//     const matchesCategory = categoryFilter === 'all' || test.category === categoryFilter;
//     return matchesSearch && matchesCategory;
//   });

//   const handleCategoryChange = (event: SelectChangeEvent) => {
//     setCategoryFilter(event.target.value);
//   };

//   const calculateTotal = () => {
//     return selectedTests.reduce((sum, test) => sum + (test.price || 0), 0);
//   };

//   return (
//     <Grid container spacing={2}>
//       {/* Left Column - Test List */}
//       <Grid size={{ xs: 12, md: 8 }}>
//         <Paper variant="outlined" sx={{ p: 2 }}>
//           <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
//             <TextField
//               sx={{ flex: 2, minWidth: '200px' }}
//               placeholder="Search tests by name or code..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               InputProps={{
//                 startAdornment: (
//                   <InputAdornment position="start">
//                     <SearchIcon />
//                   </InputAdornment>
//                 ),
//               }}
//               size="small"
//             />
            
//             <FormControl sx={{ flex: 1, minWidth: '150px' }} size="small">
//               <InputLabel>Category</InputLabel>
//               <Select
//                 value={categoryFilter}
//                 label="Category"
//                 onChange={handleCategoryChange}
//               >
//                 {categories.map((cat) => (
//                   <MenuItem key={cat} value={cat}>
//                     {cat === 'all' ? 'All Categories' : cat}
//                   </MenuItem>
//                 ))}
//               </Select>
//             </FormControl>
//           </Box>

//           {loading ? (
//             <Box sx={{ p: 3, textAlign: 'center' }}>
//               <Typography color="textSecondary">Loading tests...</Typography>
//             </Box>
//           ) : (
//             <List sx={{ maxHeight: 400, overflow: 'auto', bgcolor: 'background.paper' }}>
//               {filteredTests.length === 0 ? (
//                 <ListItem>
//                   <ListItemText 
//                     primary="No tests found" 
//                     secondary="Try adjusting your search or category filter"
//                   />
//                 </ListItem>
//               ) : (
//                 filteredTests.map((test) => {
//                   const isSelected = selectedTests.some(t => t.id === test.id);
                  
//                   return (
//                     <React.Fragment key={test.id}>
//                       <ListItem 
//                         onClick={() => onTestSelect(test)}
//                         sx={{
//                           cursor: 'pointer',
//                           bgcolor: isSelected ? 'action.selected' : 'inherit',
//                           '&:hover': {
//                             bgcolor: isSelected ? 'action.selected' : 'action.hover',
//                           }
//                         }}
//                       >
//                         <ListItemText
//                           primary={
//                             <Box display="flex" alignItems="center" gap={1}>
//                               <ScienceIcon fontSize="small" color="primary" />
//                               <Typography variant="body1">
//                                 {test.testName}
//                               </Typography>
//                               {test.category && (
//                                 <Chip 
//                                   label={test.category} 
//                                   size="small" 
//                                   variant="outlined"
//                                   sx={{ ml: 1 }}
//                                 />
//                               )}
//                             </Box>
//                           }
//                           secondary={
//                             <Box>
//                               <Typography variant="caption" color="textSecondary">
//                                 Code: {test.testCode}
//                               </Typography>
//                               {test.price > 0 && (
//                                 <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
//                                   ₹{test.price}
//                                 </Typography>
//                               )}
//                             </Box>
//                           }
//                         />
//                         <ListItemSecondaryAction>
//                           <Checkbox
//                             edge="end"
//                             checked={isSelected}
//                             onChange={() => onTestSelect(test)}
//                             color="primary"
//                           />
//                         </ListItemSecondaryAction>
//                       </ListItem>
//                       <Divider />
//                     </React.Fragment>
//                   );
//                 })
//               )}
//             </List>
//           )}
//         </Paper>
//       </Grid>

//       {/* Right Column - Selected Tests Summary */}
//       <Grid size={{ xs: 12, md: 4 }}>
//         <Paper variant="outlined" sx={{ p: 2, position: 'sticky', top: 20 }}>
//           <Typography variant="h6" gutterBottom>
//             Selected Tests
//             <Chip 
//               label={selectedTests.length} 
//               size="small" 
//               color="primary" 
//               sx={{ ml: 1 }}
//             />
//           </Typography>
          
//           {selectedTests.length === 0 ? (
//             <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
//               No tests selected yet
//             </Typography>
//           ) : (
//             <>
//               <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
//                 {selectedTests.map((test) => (
//                   <ListItem key={test.id}>
//                     <ListItemText
//                       primary={test.testName}
//                       secondary={test.testCode}
//                     />
//                     <Typography variant="body2" color="primary">
//                       ₹{test.price}
//                     </Typography>
//                     <Checkbox
//                       edge="end"
//                       checked={true}
//                       onChange={() => onTestSelect(test)}
//                       size="small"
//                       sx={{ ml: 1 }}
//                     />
//                   </ListItem>
//                 ))}
//               </List>
//               <Divider sx={{ my: 1 }} />
//               <Box display="flex" justifyContent="space-between" alignItems="center">
//                 <Typography variant="subtitle1">Total:</Typography>
//                 <Typography variant="h6" color="primary">
//                   ₹{calculateTotal()}
//                 </Typography>
//               </Box>
//             </>
//           )}
//         </Paper>
//       </Grid>
//     </Grid>
//   );
// };